import createPersistedState from 'vuex-persistedstate'
import sentryUserPlugin from './sentryUserPlugin'
import dayjs from '@shared/services/DayJS'
const uuidv7 = require('uuidv7')

// The ONLY state that is persisted to localStorage — an explicit allowlist, not a
// denylist. Every key here is a small, device/session-level value owned by this
// shared root store (auth + PKCE, current location/site, sidebar + datagrid UI
// prefs, theme, the report date range). Everything NOT listed is dropped from
// persistence — most importantly the namespaced Vuex modules that the portal apps
// register into this store (ProductCatalog, Production, Distribution, …). Those
// hold fully-hydrated, server-fetched collections that scale with data volume and
// are refetched on every mount, so persisting them was pure cost — and on a large
// product one of them (CatalogForm's variant arrays) blew the ~5MB quota and threw
// out of store.commit(). An allowlist is the durable fix: a denylist has to be
// extended for every new heavy key, whereas here any future registered module is
// excluded by default and has to opt in deliberately.
//
// Deliberately NOT persisted: transient runtime state. `theDirtyDetails` is the
// clearest case — the DetectFormChange mixin mirrors the *entire* edited form into
// theDirtyDetails.formChangeData on every keystroke, so on a large form it can grow
// to megabytes; it exists only to drive the in-session unsaved-changes nav guard
// and is meaningless after a reload, so it must stay out of the allowlist.
//
// vuex-persistedstate applies this as `reducer(state, paths)` on every mutation,
// which is a reference pick of these top-level keys (no deep tree walk, so it can
// never throw on a cycle); the JSON.stringify + write is guarded in setState below.
export const PERSISTED_STATE_KEYS = [
  'appVersion',
  'authToken',
  'currentCoreLocation',
  'currentCoreLocationName',
  'currentCoreLocationObj',
  'currentLocation',
  'currentLocationDispensary',
  'currentLocationWholesale',
  'currentLocationContext',
  'currentSite',
  'dateRange',
  'isImpersonating',
  'loginRedirect',
  'pkce',
  'sessionId',
  'sidebarMinimize',
  'sidebarShow',
  'sidebarUnfoldable',
  'sidebarVisible',
  'uiSettingsDatagridHeight',
  'uiSettingsDatagridTextSize',
  'uiThemeMode',
  'username',
  'userProfile',
]

// A localStorage wrapper whose every operation is non-throwing. vuex-persistedstate
// touches storage in three unguarded places — an assertStorage probe when the plugin
// is attached to the store, the getState read on init, and the setState write on
// every mutation — and Vuex 3 runs all of them with no try/catch of its own. A
// locked-down or full store (private mode, enterprise policy, exhausted quota) can
// therefore throw out of any of them, including at boot. Routing every access through
// this adapter guarantees a storage failure degrades to a warning and can never break
// app control flow. `window.localStorage` is read lazily inside each method so the
// adapter is safe to construct before `window` exists (SSR / unit tests).
const PERSIST_SIZE_WARN_BYTES = 100 * 1024 // ~100KB. The ~5MB browser quota is the cliff, not the target.
const persistWarnedKeys = new Set()

function persistWarn(message, err) {
  // eslint-disable-next-line no-console
  console.warn(message, err === undefined ? '' : err)
}

const safeStorage = {
  getItem(key) {
    try {
      return window.localStorage.getItem(key)
    } catch (e) {
      return null
    }
  },
  setItem(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch (e) {
      persistWarn(`Could not persist store to localStorage['${key}'] (storage full or unavailable):`, e)
    }
  },
  removeItem(key) {
    try {
      window.localStorage.removeItem(key)
    } catch (e) {
      // best-effort; nothing to recover
    }
  },
}

export default {
  state: {
    appVersion: null,
    authToken: null,
    currentCoreLocation: null,
    currentCoreLocationName: null,
    currentCoreLocationObj: null,
    currentLocation: null,
    currentLocationDispensary: null,
    currentLocationWholesale: null,
    currentLocationContext: 'dispensary', // Track current context
    currentSite: null,
    dateRange: {
      startDate: dayjs().subtract(7, 'days').toDate(),
      endDate: dayjs().toDate()
    },
    isImpersonating: false,
    loginRedirect: null,
    pkce: null,
    sessionId: uuidv7, // Unique session ID for each user session
    sidebarMinimize: false,
    sidebarShow: 'responsive',
    sidebarUnfoldable: false,
    sidebarVisible: '',
    // for subheader that shows when a form is dirty
    theDirtyDetails: {
      formChangeData: {},
      isDirty: false,
      maskedFormDataKeys: [], //maskedFormDataKeys: ['ispAccountPin','voipAutoAttendantPin','username','passPhrase'],
    },
    uiSettingsDatagridHeight: '700',
    uiSettingsDatagridTextSize: 'md',
    uiThemeMode: 'light',
    username: null,
    userProfile: null,
  },
  actions: {
    async generatePkce({ commit }) {
      // Helper functions defined within the action
      function generateCodeVerifier() {
        const array = new Uint32Array(32);
        window.crypto.getRandomValues(array);
        return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
      }

      async function generateCodeChallenge(verifier) {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        return btoa(String.fromCharCode(...new Uint8Array(digest)))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      }

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      commit('pkce', {
        codeVerifier,
        codeChallenge
      });
    },
  },
  getters: {
    appVersion(state) {
      return state.appVersion
    },
    authToken(state) {
      return state.authToken
    },
    currentCoreLocation(state) {
      return state.currentCoreLocation
    },
    currentCoreLocationName(state) {
      return state.currentCoreLocationName
    },
    currentCoreLocationObj(state) {
      return state.currentCoreLocationObj
    },
    currentLocation(state) {
      // Intelligently return the appropriate location based on current context
      if (state.currentLocationContext === 'wholesale') {
        return state.currentLocationWholesale || state.currentLocation
      }
      return state.currentLocationDispensary || state.currentLocation
    },
    currentLocationDispensary(state) {
      return state.currentLocationDispensary
    },
    currentLocationWholesale(state) {
      return state.currentLocationWholesale
    },
    currentLocationContext(state) {
      return state.currentLocationContext
    },
    currentSite(state) {
      return state.currentSite
    },
    dateRange(state) {
      return state.dateRange
    },
    isImpersonating(state) {
      return state.isImpersonating
    },
    loginRedirect(state) {
      return state.loginRedirect
    },
    pkce(state) {
      return state.pkce
    },
    sessionId(state) {
      return state.sessionId
    },
    theDirtyDetails(state) {
      return state.theDirtyDetails
    },
    uiSettingsDatagridHeight(state) {
      return state.uiSettingsDatagridHeight
    },
    uiSettingsDatagridTextSize(state) {
      return state.uiSettingsDatagridTextSize
    },
    uiThemeMode(state) {
      return state.uiThemeMode
    },
    userProfile(state) {
      return state.userProfile
    },
    username(state) {
      return state.username
    },
  },
  mutations: {
    appVersion(state, value) {
      state.appVersion = value
    },
    authToken(state, value) {
      state.authToken = value
    },
    currentCoreLocation(state, value) {
      state.currentCoreLocation = value
    },
    currentCoreLocationName(state, value) {
      state.currentCoreLocationName = value
    },
    currentCoreLocationObj(state, value) {
      state.currentCoreLocationObj = value
    },
    currentLocation(state, value) {
      // Intelligently route to the appropriate location based on current context
      state.currentLocation = value // Keep for backward compatibility
      if (state.currentLocationContext === 'wholesale') {
        state.currentLocationWholesale = value
      } else {
        state.currentLocationDispensary = value
      }
    },
    currentLocationDispensary(state, value) {
      state.currentLocationDispensary = value
    },
    currentLocationWholesale(state, value) {
      state.currentLocationWholesale = value
    },
    currentLocationContext(state, value) {
      state.currentLocationContext = value
    },
    dateRange(state, value) {
      state.dateRange = value
    },
    isImpersonating(state, value) {
      state.isImpersonating = value
    },
    loginRedirect(state, value) {
      state.loginRedirect = value
    },
    pkce(state, value) {
      state.pkce = value
    },
    reset(state) {
      state.appVersion = null
      state.authToken = null
      state.currentCoreLocation = null
      state.currentCoreLocationName = null
      state.currentCoreLocationObj = null
      state.currentLocation = null
      state.currentLocationDispensary = null
      state.currentLocationWholesale = null
      state.currentLocationContext = 'dispensary'
      state.isImpersonating = false
      state.loginRedirect = null
      state.pkce = null
      state.theDirtyDetails = {
        formChangeData: {},
        isDirty: false,
        maskedFormDataKeys: [], //maskedFormDataKeys: ['ispAccountPin','voipAutoAttendantPin','username','passPhrase'],
      }
      state.userProfile = null
      state.uiSettingsDatagridHeight = '700'
      state.uiSettingsDatagridTextSize = 'md'
      // uiThemeMode is intentionally NOT reset on logout: the theme is a
      // device-level preference (persisted to localStorage['eden-theme'], shared
      // with the Vue 3 modules) and should survive logout like OS dark mode.
      state.username = null
    },
    sessionId(state, value) {
      state.sessionId = value
    },
    set(state, [variable, value]) {
      state[variable] = value
    },
    theDirtyDetails(state, value) {
      state.theDirtyDetails = value
    },
    toggleSidebarDesktop(state) {
      const sidebarOpened = [true, 'responsive'].includes(state.sidebarShow)
      state.sidebarShow = sidebarOpened ? false : 'responsive'
    },
    toggleSidebarMobile(state) {
      const sidebarClosed = [false, 'responsive'].includes(state.sidebarShow)
      state.sidebarShow = sidebarClosed ? true : 'responsive'
    },
    uiSettingsDatagridHeight(state, value) {
      state.uiSettingsDatagridHeight = value
    },
    uiSettingsDatagridTextSize(state, value) {
      state.uiSettingsDatagridTextSize = value
    },
    uiThemeMode(state, value) {
      state.uiThemeMode = value
    },
    userProfile(state, value) {
      state.userProfile = value
    },
    username(state, value) {
      state.username = value
    },
  },
  modules: {},
  plugins: [
    createPersistedState({
      key: 'vuex-portal',
      // Every storage access goes through safeStorage so a storage failure — quota,
      // private mode, enterprise lockdown — can never throw out of a mutation or out
      // of the assertStorage probe at boot. See safeStorage above.
      storage: safeStorage,
      // Allowlist: only these top-level keys are persisted (see PERSISTED_STATE_KEYS).
      paths: PERSISTED_STATE_KEYS,
      // Read side of the allowlist. `paths` only governs WRITES; on init
      // vuex-persistedstate reads this blob and merges every key it contains back
      // into the store. A returning user still holds the pre-allowlist blob — with
      // the heavy namespaced module collections in it — so without this filter the
      // first post-deploy load would rehydrate all of that stale state (briefly
      // re-inflating memory and flashing stale rows before each form's mounted()
      // refetch). Filtering reads to the same allowlist drops those stale keys on
      // the way in, permanently, without renaming the key — so auth, location, and
      // theme survive the transition and nobody is logged out.
      getState: (key, storage) => {
        let value
        try {
          const raw = storage.getItem(key)
          value = raw ? JSON.parse(raw) : undefined
        } catch (e) {
          return undefined // unreadable / corrupt JSON — boot with defaults
        }
        if (!value || typeof value !== 'object') return value
        return PERSISTED_STATE_KEYS.reduce((acc, k) => {
          if (k in value) acc[k] = value[k]
          return acc
        }, {})
      },
      // Serialize, warn if the payload has outgrown its budget, then hand off to
      // safeStorage (which is non-throwing). JSON.stringify is guarded separately
      // because a circular value would throw here, before the write — the only
      // failure safeStorage can't catch for us. The `paths` pick upstream is a
      // reference grab of small keys, so it can't throw.
      setState: (key, state, storage) => {
        let json
        try {
          json = JSON.stringify(state)
        } catch (e) {
          persistWarn(`Could not serialize store for localStorage['${key}']:`, e)
          return
        }
        // Size budget: the allowlist should keep this well under ~100KB. Crossing it
        // means a persisted key is growing unbounded (the failure mode #1154 hit, but
        // via an allowlisted key) — surface it long before the ~5MB quota cliff. Warn
        // once per key so a per-mutation write does not spam the console.
        if (json.length > PERSIST_SIZE_WARN_BYTES && !persistWarnedKeys.has(key)) {
          persistWarnedKeys.add(key)
          persistWarn(
            `Persisted store localStorage['${key}'] is ~${Math.round(json.length / 1024)}KB, over the ` +
            `${PERSIST_SIZE_WARN_BYTES / 1024}KB budget — a persisted key is likely growing unbounded; ` +
            `check PERSISTED_STATE_KEYS.`,
          )
        }
        storage.setItem(key, json)
      },
    }),
    // Must stay AFTER createPersistedState: sentryUserPlugin seeds the staff
    // identity from store.state.userProfile at boot, which relies on
    // createPersistedState having synchronously rehydrated it first.
    sentryUserPlugin,
  ],
}
